/**
 * Control-plane calls the Stream Manager and the Channel settings make outside a broadcast:
 * the channel's public profile, and the figures the dashboard strip shows (viewers, followers).
 * Authenticated calls carry the stream key, whose channel id is the one the profile belongs to.
 */
import type { ChannelGoal, ChannelProfile, ClipSettings, FeaturedContent, RaidSettings, ScheduleEntry } from '../core/model';
import { describeSwarmFailure, parseStreamKey, SWARM_NETWORK_FAILURE } from './swarm-publisher';
import { isTokenSafeEndpoint } from './whip-http';

const REQUEST_TIMEOUT_MS = 10_000;

export interface ChannelApiDeps {
  fetch?: typeof fetch;
}

/** A channel as the control plane returns it (ompbackend `ChannelResponse`), the fields the studio reads. */
export interface RemoteChannel {
  channelId: string;
  name: string;
  displayName?: string | null;
  slug: string;
  description: string;
  avatarUrl: string | null;
  isLive: boolean;
  followerCount: number;
  links?: { title: string; url: string }[];
  accentColor?: string | null;
  bannerUrl?: string | null;
  offlineImageUrl?: string | null;
  schedule?: ScheduleEntry[];
  featured?: FeaturedContent;
  goal?: ChannelGoal | null;
  raids?: RaidSettings;
  clips?: ClipSettings;
  stream: RemoteStream | null;
}

/** A stream as the control plane returns it (ompbackend `StreamInfo`), the fields the studio reads. */
export interface RemoteStream {
  streamId: string;
  channelId: string;
  channelSlug: string | null;
  channelName: string | null;
  title: string;
  category: string | null;
  tags: string[];
  language: string | null;
  status: 'created' | 'starting' | 'live' | 'ending' | 'offline';
  viewerCount: number;
  startedAt: string | null;
}

/** What `PATCH /api/channels/:id` accepts; '' clears a URL, the backend stores null. */
export interface ChannelProfilePatch {
  /** null clears it: the channel shows its registered name again. */
  displayName?: string | null;
  description?: string;
  avatarUrl?: string | null;
  links?: { title: string; url: string }[];
  accentColor?: string;
  bannerUrl?: string | null;
  offlineImageUrl?: string | null;
  schedule?: ScheduleEntry[];
  featured?: FeaturedContent;
  goal?: ChannelGoal | null;
  raids?: RaidSettings;
  clips?: Partial<ClipSettings>;
}

/** The parts of the channel settings that are published together. */
export interface PublishedChannel {
  profile: ChannelProfile;
  schedule: ScheduleEntry[];
  featured: FeaturedContent;
  goal: ChannelGoal | null;
  raids: RaidSettings;
  clips: ClipSettings;
}

export function profileToPatch(profile: ChannelProfile, extra?: Omit<PublishedChannel, 'profile'>): ChannelProfilePatch {
  return {
    ...(extra ? { schedule: extra.schedule, featured: extra.featured, goal: extra.goal, raids: extra.raids, clips: extra.clips } : {}),
    displayName: profile.displayName || null,
    description: profile.bio,
    avatarUrl: profile.avatarUrl || null,
    links: profile.links,
    accentColor: profile.accentColor,
    bannerUrl: profile.bannerUrl || null,
    offlineImageUrl: profile.offlineImageUrl || null,
  };
}

const base = (apiUrl: string): string => apiUrl.trim().replace(/\/+$/, '');

function requireTokenSafe(apiUrl: string): string {
  const root = base(apiUrl);
  if (!isTokenSafeEndpoint(root)) {
    throw new Error('The swarm API URL must be https:// (or http:// to localhost): the stream key would otherwise travel in clear.');
  }
  return root;
}

async function send(doFetch: typeof fetch, url: string, init: RequestInit): Promise<Response> {
  try {
    return await doFetch(url, { ...init, credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) });
  } catch {
    throw new Error(SWARM_NETWORK_FAILURE.message);
  }
}

async function errorCode(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.json()) as { code?: unknown };
    return typeof body.code === 'string' ? body.code : undefined;
  } catch {
    return undefined;
  }
}

/** Pushes the profile to the channel the stream key belongs to. Resolves with the channel as stored. */
export async function publishChannelProfile(
  options: { apiUrl: string; streamKey: string; profile: ChannelProfile; extra?: Omit<PublishedChannel, 'profile'> },
  deps: ChannelApiDeps = {},
): Promise<RemoteChannel> {
  const doFetch = deps.fetch ?? ((input, init) => fetch(input, init));
  const key = parseStreamKey(options.streamKey);
  if (!key) throw new Error('Paste your ompeer.com stream key in Settings → Stream first.');
  const root = requireTokenSafe(options.apiUrl);
  const res = await send(doFetch, `${root}/api/channels/${encodeURIComponent(key.channelId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${options.streamKey.trim()}` },
    body: JSON.stringify(
      profileToPatch(options.profile, options.extra),
    ),
  });
  if (!res.ok) {
    const code = await errorCode(res);
    if (res.status === 404 && code === 'UNSUPPORTED') throw new Error('This ompeer.com server does not accept channel profiles yet.');
    throw new Error(describeSwarmFailure(res.status, code).message);
  }
  return (await res.json()) as RemoteChannel;
}

/** The stream the key names, as the control plane sees it (null when it does not know it). */
export async function fetchStreamInfo(options: { apiUrl: string; streamKey: string }, deps: ChannelApiDeps = {}): Promise<RemoteStream | null> {
  const doFetch = deps.fetch ?? ((input, init) => fetch(input, init));
  const key = parseStreamKey(options.streamKey);
  if (!key) return null;
  const res = await send(doFetch, `${base(options.apiUrl)}/api/streams/${encodeURIComponent(key.streamId)}`, { method: 'GET' });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(describeSwarmFailure(res.status, await errorCode(res)).message);
  return (await res.json()) as RemoteStream;
}

/** The channel by slug, with its current stream; null when unknown. */
export async function fetchChannel(options: { apiUrl: string; slug: string }, deps: ChannelApiDeps = {}): Promise<RemoteChannel | null> {
  const doFetch = deps.fetch ?? ((input, init) => fetch(input, init));
  const res = await send(doFetch, `${base(options.apiUrl)}/api/channels/${encodeURIComponent(options.slug)}`, { method: 'GET' });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(describeSwarmFailure(res.status, await errorCode(res)).message);
  return (await res.json()) as RemoteChannel;
}

/** `GET /api/streams/:id/brand-safety`: the ad server's last brand safety rating of the live broadcast. */
export type BrandSafety =
  | { available: false }
  | {
      available: true;
      rating: 'safe' | 'unsafe' | 'unknown';
      /** Safe, with excluded categories in doubt that do not block (only a clear detection excludes). */
      doubts?: boolean;
      score: number;
      categories: string[];
      evaluated_at: string;
      window_minutes: number;
      reason: string;
      /** Ads are off for the channel now. Absent from servers before 2026-10-23. */
      blocked?: boolean;
      block_reason?: 'recurrent_channel' | 'positive_this_live' | null;
      /** RFC 3339: the earliest time ads resume if nothing new is detected; null when unknown. */
      recovers_at?: string | null;
    };

/**
 * The brand safety of the key's stream (channel stream key). Offline it answers the channel-level block the ad
 * server keeps (e.g. recurrence before go-live), else unavailable. Throws on a network failure or a refusal.
 */
export async function fetchBrandSafety(options: { apiUrl: string; streamKey: string }, deps: ChannelApiDeps = {}): Promise<BrandSafety> {
  const doFetch = deps.fetch ?? ((input, init) => fetch(input, init));
  const key = parseStreamKey(options.streamKey);
  if (!key) throw new Error('Paste your ompeer.com stream key in Settings → Stream first.');
  const root = requireTokenSafe(options.apiUrl);
  const res = await send(doFetch, `${root}/api/streams/${encodeURIComponent(key.streamId)}/brand-safety`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${options.streamKey.trim()}` },
  });
  if (!res.ok) throw new Error(describeSwarmFailure(res.status, await errorCode(res)).message);
  return (await res.json()) as BrandSafety;
}

/** `https://ompeer.com/channel/<slug>`. */
export function channelPageUrl(portalUrl: string, slug: string): string {
  return `${portalUrl.replace(/\/+$/, '')}/channel/${encodeURIComponent(slug)}`;
}

/** The portal's chat-only page for the room, for embedding in the Stream Manager. */
export function popoutChatUrl(portalUrl: string, room: string): string {
  return `${portalUrl.replace(/\/+$/, '')}/popout/${encodeURIComponent(room)}/chat`;
}

export interface RaidResult {
  ok: true;
  /** Slug of the channel the viewers were sent to. */
  target: string;
  viewers: number;
}

/**
 * Sends the live stream's viewers to another channel and ends the stream (`POST /api/streams/:id/raid`).
 * The target's raid settings decide; a refusal is a 403 with a `code` the message names.
 */
export async function raidChannel(options: { apiUrl: string; streamKey: string; target: string }, deps: ChannelApiDeps = {}): Promise<RaidResult> {
  const doFetch = deps.fetch ?? ((input, init) => fetch(input, init));
  const key = parseStreamKey(options.streamKey);
  if (!key) throw new Error('Paste your ompeer.com stream key in Settings → Stream first.');
  const root = requireTokenSafe(options.apiUrl);
  const res = await send(doFetch, `${root}/api/streams/${encodeURIComponent(key.streamId)}/raid`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${options.streamKey.trim()}` },
    body: JSON.stringify({ target: options.target.trim().toLowerCase() }),
  });
  if (!res.ok) {
    const code = await errorCode(res);
    if (res.status === 403 && code) throw new Error(RAID_REFUSALS[code] ?? `The raid was refused (${code}).`);
    if (res.status === 404 && code === 'TARGET_NOT_FOUND') throw new Error('ompeer.com does not know that channel.');
    throw new Error(describeSwarmFailure(res.status, code).message);
  }
  return (await res.json()) as RaidResult;
}

const RAID_REFUSALS: Record<string, string> = {
  RAID_REJECTED: 'That channel does not accept raids.',
  RAID_NOT_FOLLOWING: 'That channel only accepts raids from channels it follows.',
  RAID_TOO_FEW_VIEWERS: 'That channel needs more viewers than you have to accept a raid.',
  RAID_TOO_MANY_VIEWERS: 'That channel caps the viewers it accepts from a raid, and you have more.',
  RAID_CHANNEL_TOO_NEW: 'That channel only accepts raids from older channels.',
  RAID_SELF: 'You cannot raid your own channel.',
  NOT_LIVE: 'Start streaming before raiding.',
};

/** A category as `GET /api/categories/search` returns it. */
export interface CategorySuggestion {
  slug: string;
  name: string;
  source: 'catalog' | 'custom';
  viewerCount: number;
  streamCount: number;
}

/** Categories matching `q` (public, no stream key). Throws on a network failure or a refusal. */
export async function searchCategories(
  options: { apiUrl: string; q: string; limit?: number },
  deps: ChannelApiDeps = {},
): Promise<CategorySuggestion[]> {
  const doFetch = deps.fetch ?? ((input, init) => fetch(input, init));
  const query = new URLSearchParams({ q: options.q, limit: String(options.limit ?? 8) });
  const res = await doFetch(`${base(options.apiUrl)}/api/categories/search?${query.toString()}`, {
    method: 'GET',
    credentials: 'omit',
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  if (!res.ok) throw new Error(describeSwarmFailure(res.status, await errorCode(res)).message);
  const body = (await res.json()) as { categories?: unknown };
  if (!Array.isArray(body.categories)) return [];
  return body.categories.filter(
    (c): c is CategorySuggestion => typeof c === 'object' && c !== null && typeof (c as CategorySuggestion).name === 'string' && typeof (c as CategorySuggestion).slug === 'string',
  );
}
